import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableContainer,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'

import { RootState } from 'store'
import { replaceTripTasks, selectOrderId } from 'store/trips/trips.slice'
import { useSessionOrderEventCleanup } from 'store/trips/use-session-order-event-cleanup'
import { setOrders } from 'store/orders/orders.slice'
import { EquipmentResponse } from 'store/equipments/equipments.types'
import { useListEquipmentsQuery } from 'store/equipments/equipments.api'
import {
  useListActiveOrdersQuery,
  useListNewOrdersQuery,
} from 'store/orders/orders.api'
import { Order } from 'store/orders/orders.types'
import { TripPayload } from 'store/trips/trips.types'
import DropFormDialog from 'components/drop-form-dialog/drop-form-dialog.component'
import HookFormDialog from 'components/hook-form-dialog/hook-form-dialog.component'
import TripBuilderAddButton from 'pages/trip/create-trip/trip-builder-selection/trip-builder-add-button'
import TripBuilderSelectionPanel from 'pages/trip/create-trip/trip-builder-selection/trip-builder-selection-panel'
import { TripBuilderSelectionMode } from 'pages/trip/create-trip/trip-builder-selection/trip-builder-selection.types'
import TripDetailsTableHeaders from 'pages/trip/create-trip/trip-details-old/trip-details-table/trip-details-table-header/trip-details-table-header'
import TripDetailsTableBody from 'pages/trip/create-trip/trip-details-old/trip-details-table/trip-details-table-body/trip-details-table-body'
import {
  getDeliveryTasksMissingPickup,
  getOrdersMissingDelivery,
} from 'pages/trip/create-trip/trip-details-old/trip-details-table/trip-details-table.utils'

type CarrierTripBuilderDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  initialSelectionMode?: Exclude<TripBuilderSelectionMode, null> | null
  initialEditingOrders?: boolean
}

type CarrierTripBuilderDialogContentProps = {
  dialogOpen: boolean
  onDiscard: () => void
  onConfirm: () => void
  initialSelectionMode?: Exclude<TripBuilderSelectionMode, null> | null
  initialEditingOrders?: boolean
}

function CarrierTripBuilderDialogContent({
  dialogOpen,
  onDiscard,
  onConfirm,
  initialSelectionMode = null,
  initialEditingOrders = false,
}: CarrierTripBuilderDialogContentProps) {
  const dispatch = useDispatch()
  const tripTasks = useSelector(
    (state: RootState) => state.trips.tripsPayload.tripTasks
  )
  const hasTripEvents = !!tripTasks && tripTasks.length > 0
  const { newOrders, activerOrders } = useSelector(
    (state: RootState) => state.orders
  )

  const [selectionMode, setSelectionMode] =
    useState<TripBuilderSelectionMode>(null)
  const [replaceEquipment, setReplaceEquipment] = useState(false)
  const [replaceTrailerId, setReplaceTrailerId] = useState<string | null>(null)
  const [targetOrderTrailerId, setTargetOrderTrailerId] = useState<string | null>(
    null
  )
  const [isEditingOrders, setIsEditingOrders] = useState(false)
  const [dropAddressOpen, setDropAddressOpen] = useState(false)
  const [hookDeliveryTask, setHookDeliveryTask] = useState<TripPayload | null>(
    null
  )

  const { data: equipmentsData, refetch: refetchEquipments } =
    useListEquipmentsQuery(undefined, { skip: !dialogOpen })
  const { data: newOrdersData } = useListNewOrdersQuery(
    { page: 0, size: 100 },
    { skip: !dialogOpen }
  )
  const { data: assignedOrdersData } = useListActiveOrdersQuery(
    { page: 0, size: 100 },
    { skip: !dialogOpen }
  )

  const equipments: EquipmentResponse[] = equipmentsData ?? []

  const tripOrders = useMemo(() => {
    const merged = new Map<number, Order>()
    for (const order of newOrdersData?.orders ?? []) {
      merged.set(order.orderId, order)
    }
    for (const order of assignedOrdersData?.orders ?? []) {
      merged.set(order.orderId, order)
    }
    return Array.from(merged.values())
  }, [newOrdersData?.orders, assignedOrdersData?.orders])

  useEffect(() => {
    if (!dialogOpen) return
    refetchEquipments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen])

  useEffect(() => {
    if (tripOrders.length > 0) {
      dispatch(setOrders({ orders: tripOrders, type: 'ASSIGNED' }))
    }
  }, [dispatch, tripOrders])

  useEffect(() => {
    if (!dialogOpen) {
      setSelectionMode(null)
      setReplaceEquipment(false)
      setReplaceTrailerId(null)
      setTargetOrderTrailerId(null)
      setIsEditingOrders(false)
      return
    }

    if (initialSelectionMode) {
      setSelectionMode(initialSelectionMode)
      setIsEditingOrders(initialEditingOrders)
    }
  }, [dialogOpen, initialSelectionMode, initialEditingOrders])

  const ordersMissingDelivery = useMemo(
    () => (tripTasks?.length ? getOrdersMissingDelivery(tripTasks) : []),
    [tripTasks]
  )

  const hasDropTrailer = useMemo(
    () => tripTasks?.some((task) => task.type === 'DROP_TRAILER') ?? false,
    [tripTasks]
  )

  const hasTractor = useMemo(
    () => tripTasks?.some((task) => task.type === 'HOOK_TRACTOR') ?? false,
    [tripTasks]
  )

  const hasEquipmentTrailer = useMemo(
    () =>
      tripTasks?.some(
        (task) =>
          (task.type === 'HOOK_TRAILER' || task.type === 'DROP_TRAILER') &&
          task.orderEventId == null &&
          task.sourceOrderPkId == null &&
          !task.orderId
      ) ?? false,
    [tripTasks]
  )

  const hasPickupOrDelivery = useMemo(
    () =>
      tripTasks?.some(
        (task) =>
          task.type === 'PICKUP' ||
          task.type === 'DELIVERY' ||
          (task.type === 'HOOK_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null)) ||
          (task.type === 'DROP_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null))
      ) ?? false,
    [tripTasks]
  )

  const trailerHasOrders = (trailerId: string | null) =>
    (tripTasks ?? []).some(
      (task) =>
        (trailerId == null || task.trailerId === trailerId) &&
        (task.type === 'PICKUP' ||
          task.type === 'DELIVERY' ||
          (task.type === 'HOOK_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null)) ||
          (task.type === 'DROP_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null)))
    )

  const deliveryOnlyTask = useMemo(() => {
    if (!tripTasks) return null
    return getDeliveryTasksMissingPickup(tripTasks)[0] ?? null
  }, [tripTasks])

  const hookOrder = useMemo(() => {
    if (!hookDeliveryTask) return undefined
    return tripOrders.find(
      (order) =>
        order.orderId === hookDeliveryTask.sourceOrderPkId ||
        order.userOrderId === hookDeliveryTask.orderId
    )
  }, [hookDeliveryTask, tripOrders])

  const handleEnterDropAddress = (orderKey: string) => {
    const userOrderId = orderKey.startsWith('event:') ? undefined : orderKey
    const orderEventId = orderKey.startsWith('event:')
      ? Number(orderKey.replace('event:', ''))
      : undefined

    const order = userOrderId
      ? [...(newOrders ?? []), ...(activerOrders ?? [])].find(
          (o) => o.userOrderId === userOrderId
        )
      : [...(newOrders ?? []), ...(activerOrders ?? [])].find(
          (o) =>
            o.orderEvents?.some((e) => e.orderEventId === orderEventId) ||
            o.exceptions?.some((e) => e.orderEventId === orderEventId)
        )

    if (order) {
      dispatch(selectOrderId({ orderId: order.orderId }))
    }

    setDropAddressOpen(true)
  }

  const openSelection = (
    mode: Exclude<TripBuilderSelectionMode, null>,
    replace = false,
    editingOrders = false,
    trailerId: string | null = null
  ) => {
    setReplaceEquipment(replace)
    setReplaceTrailerId(mode === 'trailer' ? trailerId : null)
    setTargetOrderTrailerId(mode === 'order' ? trailerId : null)
    setIsEditingOrders(editingOrders)
    setSelectionMode(mode)
  }

  const closeSelection = () => {
    setSelectionMode(null)
    setReplaceEquipment(false)
    setReplaceTrailerId(null)
    setTargetOrderTrailerId(null)
    setIsEditingOrders(false)
  }

  const emptyStateButtons = (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      <TripBuilderAddButton
        label="+ Add Tractor"
        onClick={() => openSelection('tractor')}
      />
      <TripBuilderAddButton
        label="+ Add Trailer"
        onClick={() => openSelection('trailer')}
      />
      <TripBuilderAddButton
        label="+ Add Pickup & Delivery"
        onClick={() => openSelection('order')}
      />
    </Stack>
  )

  const afterHookTractorButtons =
    hasTractor && !hasEquipmentTrailer ? (
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        <TripBuilderAddButton
          label="+ Add Trailer"
          onClick={() => openSelection('trailer')}
        />
        {!hasPickupOrDelivery && (
          <TripBuilderAddButton
            label="+ Add Pickup & Delivery"
            onClick={() => openSelection('order')}
          />
        )}
      </Stack>
    ) : null

  const beforeTripButtons =
    !hasTractor && (hasEquipmentTrailer || hasPickupOrDelivery) ? (
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        <TripBuilderAddButton
          label="+ Add Tractor"
          onClick={() => openSelection('tractor')}
        />
        {!hasEquipmentTrailer && (
          <TripBuilderAddButton
            label="+ Add Trailer"
            onClick={() => openSelection('trailer')}
          />
        )}
      </Stack>
    ) : null

  const afterHookTrailerButtons = (trailerId: string | null) =>
    hasEquipmentTrailer && !trailerHasOrders(trailerId) ? (
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        <TripBuilderAddButton
          label="+ Add Pickup & Delivery"
          onClick={() => openSelection('order', false, false, trailerId)}
        />
      </Stack>
    ) : null

  const beforeDeliveryButtons = deliveryOnlyTask ? (
    <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
      <TripBuilderAddButton
        label="+ Enter Hook Trailer Address"
        onClick={() => setHookDeliveryTask(deliveryOnlyTask)}
      />
    </Stack>
  ) : null

  const beforeDropTrailerButtons = (trailerId: string | null) => {
    if (!trailerHasOrders(trailerId)) return null

    return (
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        {ordersMissingDelivery.length > 0 && (
          <TripBuilderAddButton
            label={
              hasDropTrailer
                ? '+ Edit Drop Trailer Address'
                : '+ Enter Drop Trailer Address'
            }
            onClick={() => handleEnterDropAddress(ordersMissingDelivery[0])}
          />
        )}
        <TripBuilderAddButton
          label="+ Add Another Pickup & Delivery"
          onClick={() => openSelection('order', false, false, trailerId)}
        />
      </Stack>
    )
  }

  const afterDropTrailerButtons = hasEquipmentTrailer ? (
    <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
      <TripBuilderAddButton
        label="+ Add Another Trailer"
        onClick={() => openSelection('trailer')}
      />
    </Stack>
  ) : null

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 2,
          py: 0.5,
        }}
      >
        <IconButton aria-label="close" onClick={onDiscard} size="small">
          <CloseIcon sx={{ color: 'error.main', fontSize: 32 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer>
          <Table>
            {hasTripEvents && (
              <TripDetailsTableHeaders showRowActionsColumn />
            )}
            {hasTripEvents && (
              <TripDetailsTableBody
                tripTasks={tripTasks}
                isDriverBuilder
                onOpenDropAddress={() => setDropAddressOpen(true)}
                onChangeTractor={() => openSelection('tractor', true)}
                onChangeTrailer={(trailerId) =>
                  openSelection('trailer', true, false, trailerId)
                }
                onChangePickup={(trailerId) =>
                  openSelection('order', false, true, trailerId)
                }
                beforeTripSlot={beforeTripButtons}
                afterHookTractorSlot={afterHookTractorButtons}
                afterHookTrailerSlot={afterHookTrailerButtons}
                beforeDeliverySlot={beforeDeliveryButtons}
                beforeDeliveryOrderEventId={deliveryOnlyTask?.orderEventId}
                beforeDropTrailerSlot={beforeDropTrailerButtons}
                afterDropTrailerSlot={afterDropTrailerButtons}
              />
            )}
          </Table>
        </TableContainer>

        {!hasTripEvents && emptyStateButtons}

        <Box
          sx={{
            px: 2,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={onDiscard}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 150,
              borderRadius: 1.5,
            }}
          >
            Cancel
          </Button>
          {hasTripEvents && (
            <SubmitLockButton
              variant="contained"
              color="primary"
              onClick={onConfirm}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 150,
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              Done
            </SubmitLockButton>
          )}
        </Box>
      </DialogContent>

      <DropFormDialog
        open={dropAddressOpen}
        handleClose={() => setDropAddressOpen(false)}
      />

      <HookFormDialog
        open={hookDeliveryTask != null}
        orderId={
          hookOrder?.orderId ?? hookDeliveryTask?.sourceOrderPkId ?? null
        }
        userOrderId={hookOrder?.userOrderId ?? hookDeliveryTask?.orderId ?? ''}
        deliveryOrderEventId={hookDeliveryTask?.orderEventId ?? null}
        handleClose={() => setHookDeliveryTask(null)}
      />

      <TripBuilderSelectionPanel
        open={selectionMode != null}
        mode={selectionMode}
        equipments={equipments}
        orders={tripOrders}
        replaceEquipment={replaceEquipment}
        replaceTrailerId={replaceTrailerId}
        targetOrderTrailerId={targetOrderTrailerId}
        isEditingOrders={isEditingOrders}
        onClose={closeSelection}
      />
    </>
  )
}

export function CarrierTripBuilderDialog({
  open,
  onClose,
  onConfirm,
  initialSelectionMode,
  initialEditingOrders,
}: CarrierTripBuilderDialogProps) {
  const dispatch = useDispatch()
  const { discardSessionOrderEvents } = useSessionOrderEventCleanup()
  const tripTasks = useSelector(
    (state: RootState) => state.trips.tripsPayload.tripTasks
  )
  const tripTasksSnapshotRef = useRef<TripPayload[] | null>(null)

  useEffect(() => {
    if (!open) return
    tripTasksSnapshotRef.current = structuredClone(tripTasks ?? [])
    // Snapshot once when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleDiscard = async () => {
    // Only roll back trip task selections from this popup.
    const snapshotEventIds = (tripTasksSnapshotRef.current ?? [])
      .map((task) => task.orderEventId)
      .filter((id): id is number => id != null)

    // Soft-delete drop/hook events created in this popup that won't remain after rollback
    await discardSessionOrderEvents(snapshotEventIds)

    if (tripTasksSnapshotRef.current) {
      dispatch(replaceTripTasks({ tripTasks: tripTasksSnapshotRef.current }))
    }
    onClose()
  }

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleDiscard}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '85vh',
        },
      }}
    >
      <CarrierTripBuilderDialogContent
        dialogOpen={open}
        onDiscard={handleDiscard}
        onConfirm={handleConfirm}
        initialSelectionMode={initialSelectionMode}
        initialEditingOrders={initialEditingOrders}
      />
    </Dialog>
  )
}
